import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CoverageCatalog,
  CoverageCatalogResume,
  ProductConfiguration,
  ProductCoverageCatalog,
  RequestIndicatorTypes,
} from 'data-layer-cr/dist/models';

export type CoverageProductResult = {
  coverages: CoverageCatalog[];
  coverageResumes: CoverageCatalogResume[];
  configuration: ProductConfiguration;
};

export type CoverageForProductPayload = {
  product: string;
  coverageList: CoverageCatalog[];
  configuration: ProductConfiguration;
};

export type CoverageState = {
  requestingCoverageState: RequestIndicatorTypes;
  coverages: CoverageCatalog[];
  coverageResumes: CoverageCatalogResume[];
  coveragesForProduct: ProductCoverageCatalog;
};

export const initialState: CoverageState = {
  requestingCoverageState: 'initial',
  coverages: [],
  coverageResumes: [],
  coveragesForProduct: {},
};

function mapToResume(coverage: CoverageCatalog): CoverageCatalogResume {
  return {
    code: coverage.code,
    type: coverage.type.code,
    applyDeductible: coverage.applyDeductible,
  };
}

function orderLimits(coverage: CoverageCatalog): CoverageCatalog {
  const newLimits = [...coverage.limits];
  newLimits.sort((a, b): number => a.maximum - b.maximum);
  return {
    ...coverage,
    limits: newLimits,
  };
}

const coverageSlice = createSlice({
  name: 'coverage',
  initialState,
  reducers: {
    updateRequestingCoverageState: (
      state,
      action: PayloadAction<RequestIndicatorTypes>,
    ): void => {
      state.requestingCoverageState = action.payload;
    },

    setCoverages: (state, action: PayloadAction<CoverageCatalog[]>): void => {
      state.coverages = action.payload.map(orderLimits);
      state.coverageResumes = action.payload.map(mapToResume);
    },

    setCoveragesForProduct: (
      state,
      action: PayloadAction<CoverageForProductPayload>,
    ): void => {
      const { product, coverageList, configuration } = action.payload;
      const newProductData = {
        coverages: coverageList.map(orderLimits),
        coverageResumes: coverageList.map(mapToResume),
        configuration,
      };
      state.coveragesForProduct[product] = newProductData;
    },
  },
});

export const {
  updateRequestingCoverageState,
  setCoverages,
  setCoveragesForProduct,
} = coverageSlice.actions;

export default coverageSlice.reducer;
